import { type FormEvent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SKIP_UNAUTH_HEADER, type ApiClient } from '@sentinel/api-client';
import {
  Alert,
  Button,
  DialogShell,
  FieldRow,
  Input,
  Label,
} from '@sentinel/ui';
import { SessionContext } from '../session/hooks';
import {
  StepUpMethodPicker,
  StepUpPanel,
  type StepUpMethod,
  type StepUpMethodOption,
} from './method-picker';
import { useWebAuthnAvailability } from './use-webauthn-availability';
import { runStepUpWebAuthn } from './webauthn';
import { StepUpWebAuthnPanel } from './webauthn-panel';

/** ACR value that requires a genuine second factor (TOTP / WebAuthn). */
const ACR_MFA = 'urn:sentinel:acr:mfa';

export interface StepUpMessages {
  title: string;
  description: string;
  /** Header copy while the method picker is on screen; `description` names the password. */
  descriptionChoose: string;
  passwordLabel: string;
  totpLabel?: string;
  submit: string;
  cancel: string;
  invalidPassword: string;
  genericError: string;
  methodGroupLabel: string;
  methodPasswordLabel: string;
  methodTotpLabel: string;
  methodWebauthnLabel: string;
  webauthnIntro: string;
  webauthnStart: string;
  webauthnVerifying: string;
  webauthnFailed: string;
  webauthnNoCredentials: string;
}

export interface StepUpDialogProps {
  open: boolean;
  apiClient: ApiClient;
  messages: StepUpMessages;
  /** ACR value the dialog should request (e.g. "urn:mace:incommon:iap:silver"). */
  targetAcr: string;
  /** Show the optional TOTP code input. */
  showTotp?: boolean;
  /** Called once the verify call succeeds; the caller should resolve the queue. */
  onSuccess: () => void;
  /** Called when the user cancels (Escape, Cancel button, or backdrop). */
  onCancel: () => void;
}

export function StepUpDialog({
  open,
  apiClient,
  messages,
  targetAcr,
  showTotp = false,
  onSuccess,
  onCancel,
}: StepUpDialogProps) {
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // An mfa-grade step-up can only be satisfied by a real second factor, so the
  // dialog must collect a TOTP code whenever the backend asks for it — never a
  // password alone. Password-grade step-ups show the password field only. The
  // explicit `showTotp` prop remains as an override for callers that always
  // want the field.
  const requiresMfa = targetAcr === ACR_MFA;
  const showTotpField = showTotp || requiresMfa;

  // Which local method exists follows the ACR: an mfa-grade challenge is
  // answered with a password plus an authenticator code, a password-grade one
  // with the password alone. Only ever one of the two, so the picker shows at
  // most two tabs.
  const localMethod: StepUpMethod = showTotpField ? 'totp' : 'password';

  // A user whose only second factor is a passkey has no code to type. Offer
  // the assertion whenever the account can produce one.
  const availability = useWebAuthnAvailability(apiClient, open);
  const tabbed = availability === 'available';
  const [method, setMethod] = useState<StepUpMethod>(localMethod);

  // Read through the context rather than useUser(): useSession() throws when
  // there is no SessionProvider, and this dialog has to render in unit tests
  // and in any tree that has none. No session simply means no auto-selection.
  const sessionUser = useContext(SessionContext)?.user ?? null;
  const autoSelected = useRef(false);

  // Reset form state every time the dialog opens. DialogShell focuses the
  // first focusable on mount (the password input), but explicitly refocus on
  // the next tick to handle re-opens where the dialog state didn't unmount.
  useEffect(() => {
    if (!open) return;
    setPassword('');
    setTotp('');
    setError(null);
    setSubmitting(false);
    setMethod(localMethod);
    autoSelected.current = false;
    queueMicrotask(() => passwordRef.current?.focus());
  }, [open, localMethod]);

  // Availability settles after the dialog is already open.
  //
  // If it settles to "no passkey", drop back to the local form rather than
  // leaving a selected method with no tab. If it settles to "passkey" for a
  // user with no confirmed TOTP facing an mfa-grade challenge, that passkey is
  // their only second factor — open on it, because the local panel's code
  // field is one they can never fill. `mfaEnabled` is TOTP-only: it is written
  // by TOTP confirm-enrolment and by nothing in the WebAuthn paths, which is
  // why the backend's adaptive-ACR check tests it separately from the
  // credential count. Applied once per open, so a manual switch stands.
  useEffect(() => {
    if (!tabbed) {
      if (method === 'webauthn') setMethod(localMethod);
      return;
    }
    if (autoSelected.current) return;
    autoSelected.current = true;
    if (requiresMfa && sessionUser?.mfaEnabled === false) setMethod('webauthn');
  }, [tabbed, method, localMethod, requiresMfa, sessionUser]);

  // Suppress backdrop / Escape cancel while a verify request is in flight.
  // A misclick or stray keypress during the round-trip must not strand the
  // request.
  const handleCancel = useCallback(() => {
    if (submitting) return;
    onCancel();
  }, [submitting, onCancel]);

  const handleMethodChange = useCallback((next: StepUpMethod) => {
    setMethod(next);
    setError(null);
  }, []);

  const options: StepUpMethodOption[] = [
    {
      id: localMethod,
      label: localMethod === 'totp' ? messages.methodTotpLabel : messages.methodPasswordLabel,
    },
    { id: 'webauthn', label: messages.methodWebauthnLabel },
  ];

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body: { target_acr: string; password: string; totp_code?: string } = {
        target_acr: targetAcr,
        password,
      };
      if (showTotpField && totp.trim().length > 0) body.totp_code = totp.trim();
      const res = await apiClient.POST('/auth/step-up/verify', {
        // A wrong password answers 401. Without this header the unauth
        // middleware reads that as session expiry and signs the user out,
        // so the inline error below could never be shown.
        headers: { [SKIP_UNAUTH_HEADER]: '1' },
        body,
      });
      const responseStatus = res.response?.status;
      if (res.error || (responseStatus !== undefined && responseStatus >= 400)) {
        if (responseStatus === 401 || responseStatus === 403) {
          setError(messages.invalidPassword);
        } else {
          setError(messages.genericError);
        }
        return;
      }
      onSuccess();
    } catch (err) {
      console.warn('[step-up] verify failed', err);
      setError(messages.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  async function onWebAuthnStart() {
    setError(null);
    setSubmitting(true);
    try {
      const result = await runStepUpWebAuthn(apiClient, targetAcr);
      if (result.kind === 'success') {
        onSuccess();
        return;
      }
      // A dismissed prompt is a choice, not a failure. Leave the panel as it
      // was so the user can try again or switch methods.
      if (result.kind === 'cancelled') return;
      if (result.kind === 'no_credentials') {
        setError(messages.webauthnNoCredentials);
        return;
      }
      setError(result.kind === 'rejected' ? messages.webauthnFailed : messages.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DialogShell
      open={open}
      onCancel={handleCancel}
      title={messages.title}
      description={tabbed ? messages.descriptionChoose : messages.description}
      width="narrow"
      testId="step-up-dialog"
    >
      <form onSubmit={onSubmit} style={{ display: 'grid', gap: '0.6rem' }}>
        {tabbed ? (
          <StepUpMethodPicker
            groupLabel={messages.methodGroupLabel}
            options={options}
            active={method}
            onChange={handleMethodChange}
          />
        ) : null}
        {method === 'webauthn' ? (
          <StepUpPanel tabbed={tabbed} method="webauthn">
            <StepUpWebAuthnPanel
              intro={messages.webauthnIntro}
              startLabel={messages.webauthnStart}
              verifyingLabel={messages.webauthnVerifying}
              running={submitting}
              onStart={() => {
                void onWebAuthnStart();
              }}
            />
          </StepUpPanel>
        ) : (
          <StepUpPanel tabbed={tabbed} method={localMethod}>
            <FieldRow>
              <Label htmlFor="step-up-password">{messages.passwordLabel}</Label>
              <Input
                id="step-up-password"
                ref={passwordRef}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="step-up-password"
              />
            </FieldRow>
            {showTotpField ? (
              <FieldRow>
                <Label htmlFor="step-up-totp">{messages.totpLabel ?? 'TOTP code'}</Label>
                <Input
                  id="step-up-totp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  required={requiresMfa}
                  value={totp}
                  onChange={(e) => setTotp(e.target.value)}
                  data-testid="step-up-totp"
                />
              </FieldRow>
            ) : null}
          </StepUpPanel>
        )}
        {error ? (
          <Alert type="error" role="alert" data-testid="step-up-error">
            {error}
          </Alert>
        ) : null}
        <DialogShell.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={submitting}
            data-testid="step-up-cancel"
          >
            {messages.cancel}
          </Button>
          {method === 'webauthn' ? null : (
            <Button type="submit" disabled={submitting} data-testid="step-up-submit">
              {messages.submit}
            </Button>
          )}
        </DialogShell.Footer>
      </form>
    </DialogShell>
  );
}
